import { ApplicationConfig, InjectionToken, Injectable, inject, isDevMode, provideBrowserGlobalErrorListeners, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter, withComponentInputBinding, withDebugTracing } from '@angular/router';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { TranslocoLoader, provideTransloco, translocoConfig } from '@jsverse/transloco';
import { FormioAppConfig } from '@formio/angular';

import { routes } from './app.routes';
import { EnvironmentService } from './core/services/environment.service';
import { ENVIRONMENT } from './core/tokens/environment.token';
import { environment } from '../environments/environment';
import { authInterceptor } from './core/http-interceptors/auth.interceptor';
import { myCMTokenInterceptor } from './core/http-interceptors/mycm-token.interceptor';
import { mockApiInterceptor } from './core/http-interceptors/mock-api.interceptor';
import { efusTokenInterceptor } from './core/http-interceptors/efus-token.interceptor';
import { MyCMIntegrationService } from './core/services/myCM-integration-store';

/** Injection token exposing the runtime environment config to services (matches root project pattern). */
export const ETHICO_ENV_CONFIG = new InjectionToken<EnvironmentService>('ETHICO_ENV_CONFIG');

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  constructor(private http: HttpClient) {} //nosonar

  getTranslation(lang: string) {
    const normalized = lang.replace(/^\//, '');
    return this.http.get<Record<string, string>>(`/assets/i18n/${normalized}.json`);
  }
}


export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withComponentInputBinding(), ...(isDevMode() ? [withDebugTracing()] : [])),
    provideHttpClient(withInterceptors([mockApiInterceptor, efusTokenInterceptor, authInterceptor, myCMTokenInterceptor])),
    { provide: ENVIRONMENT, useValue: environment },
    { provide: FormioAppConfig, useValue: { appUrl: '', apiUrl: 'api' } },
    { provide: ETHICO_ENV_CONFIG, useExisting: EnvironmentService },
    // Run env loading FIRST, then start the MyCM message listener.
    // Sequencing prevents the race condition where handleMessage() drops the
    // MyCM token because environmentConfig.myCMAppUrl is still null (env not
    // yet loaded) when the token arrives.
    // Both inject() calls must happen synchronously before any await.
    provideAppInitializer(async () => {
      const envService = inject(EnvironmentService);
      const myCMService = inject(MyCMIntegrationService);
      await envService.load();
      await myCMService.initializeMessageListener();
    }),
    provideTransloco({
      config: translocoConfig({
        availableLangs: [
          'en-us', 'es-es', 'ar-ae', 'fr-fr', 'de-de', 'ja-jp', 'pt-pt',
          'id-id', 'th-th', 'ko-kr', 'zh-cn', 'yue-hk', 'cs-cs', 'nl-nl',
          'it-it', 'pl-pl', 'sv-se', 'he-he', 'ru-ru', 'sl-sl', 'sr-rs',
          'ht-ht', 'hmn-cn', 'uk-ua', 'ne-np', 'ro-ro', 'tr-tr', 'vi-vn',
          'so-so', 'hi-in', 'el-el', 'ch-ch', 'fc-fc', 'pb-pb', 'tg-tg',
          'sk-sk', 'tz-ma', 'da-dk', 'hu-hu', 'mr-in', 'de1-ch', 'ga-ie',
          'kh-kh', 'ms-my', 'nb-no', 'zh1-sg', 'ta-in', 'be-by', 'pa-in',
          'my-mm', 'kk-kz', 'mk-mk', 'sw-tz', 'hr-hr', 'bg-bg', 'ca-es',
          'fi-fi', 'et-ee', 'lv-lv',
        ],
        defaultLang: 'en-us',
        fallbackLang: 'en-us',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          logMissingKey: isDevMode(),
          useFallbackTranslation: true,
        },
      }),
      loader: TranslocoHttpLoader,
    }),
  ]
};
